<template>
    <div class="file-edit-form">
        <b-card class="mb-2">
            <template #header>
                <div class="d-flex align-items-center">
                    <feather-icon icon="FileTextIcon" size="18" class="mr-50" />
                    <h5 class="mb-0">Dosya Bilgileri</h5>
                </div>
            </template>

            <b-row>
                <b-col md="6">
                    <b-form-group label="Dosya Anahtarı" label-for="fileKey">
                        <b-form-input id="fileKey" v-model="localValue.fileKey" placeholder="slider-image-1" />
                        <small class="text-muted">Kod kullanımı için benzersiz anahtar</small>
                    </b-form-group>
                </b-col>
                <b-col md="6">
                    <b-form-group label="Etiketler (Tags)" label-for="tags">
                        <b-form-input id="tags" v-model="localValue.tags" placeholder="galeri, ürünler, 2024" />
                        <small class="text-muted">Virgülle ayırarak ekleyin</small>
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="12">
                    <b-form-group label="Başlık" label-for="title">
                        <b-form-input id="title" v-model="localValue.title" placeholder="Dosya başlığı" />
                    </b-form-group>
                </b-col>
                <b-col md="12">
                    <b-form-group label="Alt Metin (SEO)" label-for="alt">
                        <b-form-input id="alt" v-model="localValue.alt" placeholder="Görsel için alternatif metin" />
                    </b-form-group>
                </b-col>
                <b-col md="12">
                    <b-form-group label="Açıklama" label-for="description">
                        <b-form-textarea id="description" v-model="localValue.description" rows="3" placeholder="Dosya hakkında detaylı açıklama" />
                    </b-form-group>
                </b-col>
            </b-row>

            <b-row>
                <b-col md="6">
                    <b-form-group label="Sıra" label-for="sortOrder">
                        <b-form-input id="sortOrder" v-model.number="localValue.sortOrder" type="number" min="0" />
                    </b-form-group>
                </b-col>
                <b-col md="6">
                    <div class="d-flex flex-column align-items-start">
                        <label class="mb-50">Durum</label>
                        <b-form-checkbox v-model="localValue.isActive" switch>
                            {{ localValue.isActive ? "Aktif" : "Pasif" }}
                        </b-form-checkbox>
                    </div>
                </b-col>
            </b-row>
        </b-card>

        <b-card class="mb-2">
            <template #header>
                <div class="d-flex align-items-center">
                    <feather-icon icon="GlobeIcon" size="18" class="mr-50" />
                    <h5 class="mb-0">Çeviriler</h5>
                </div>
            </template>

            <b-tabs pills vertical nav-wrapper-class="col-md-3" content-class="col-md-9">
                <b-tab v-for="lang in languages" :key="lang.LanguageCode" :title="lang.LanguageName">
                    <b-row>
                        <b-col md="12">
                            <b-form-group label="Başlık" :label-for="`trans-title-${lang.LanguageCode}`">
                                <b-form-input
                                    :id="`trans-title-${lang.LanguageCode}`"
                                    :value="getTranslation(lang.LanguageCode).title"
                                    @input="updateTranslation(lang.LanguageCode, 'title', $event)"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col md="12">
                            <b-form-group label="Alt Metin" :label-for="`trans-alt-${lang.LanguageCode}`">
                                <b-form-input :id="`trans-alt-${lang.LanguageCode}`" :value="getTranslation(lang.LanguageCode).alt" @input="updateTranslation(lang.LanguageCode, 'alt', $event)" />
                            </b-form-group>
                        </b-col>
                        <b-col md="12">
                            <b-form-group label="Açıklama" :label-for="`trans-desc-${lang.LanguageCode}`">
                                <b-form-textarea
                                    :id="`trans-desc-${lang.LanguageCode}`"
                                    :value="getTranslation(lang.LanguageCode).description"
                                    rows="3"
                                    @input="updateTranslation(lang.LanguageCode, 'description', $event)"
                                />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </b-tab>
            </b-tabs>
        </b-card>

        <b-card class="mb-2">
            <template #header>
                <div class="d-flex justify-content-between align-items-center">
                    <div class="d-flex align-items-center">
                        <feather-icon icon="DatabaseIcon" size="18" class="mr-50" />
                        <h5 class="mb-0">Meta Verileri (Opsiyonel)</h5>
                    </div>
                </div>
            </template>

            <div class="mb-2">
                <b-button variant="primary" size="sm" @click="addMetadata">
                    <feather-icon icon="PlusIcon" size="14" class="mr-25" />
                    Meta Ekle
                </b-button>
            </div>

            <div class="metadata-section">
                <div v-if="!localValue.metaData || localValue.metaData.length === 0" class="border rounded p-4 text-center theme-item">
                    <feather-icon icon="DatabaseIcon" size="32" class="text-muted mb-1" />
                    <p class="text-muted mb-0">Henüz metadata eklenmedi.</p>
                </div>

                <b-row v-else>
                    <b-col v-for="(meta, index) in localValue.metaData" :key="index" cols="12" md="6" class="mb-2">
                        <div class="border rounded p-2 bg-white shadow-sm d-flex flex-column flex-md-row align-items-md-center justify-content-between position-relative hover-card h-100 theme-item">
                            <div class="d-flex flex-row flex-md-column align-items-center align-items-md-start mr-md-2 mb-2 mb-md-0" style="min-width: 100px">
                                <div class="mb-1">
                                    <b-badge variant="light-primary" class="mr-1">{{ meta.MetaGroup || "Genel" }}</b-badge>
                                    <b-badge v-if="meta.LanguageCode" variant="light-info">{{ meta.LanguageCode }}</b-badge>
                                </div>
                                <div class="text-muted small d-flex align-items-center" style="font-size: 0.75rem">
                                    <feather-icon icon="ListIcon" size="10" class="mr-50" />
                                    <span
                                        >Sıra: <b>{{ meta.SortOrder }}</b></span
                                    >
                                </div>
                            </div>

                            <div class="d-flex flex-column flex-md-row flex-grow-1 mr-md-1 border-left-md pl-md-2" style="min-width: 0">
                                <div class="mb-1 mb-md-0 mr-md-2" style="flex: 0 0 35%; min-width: 0">
                                    <label class="d-block text-muted small font-weight-bold text-uppercase mb-0" style="font-size: 0.65rem">Anahtar</label>
                                    <div class="text-truncate font-weight-bolder text-dark small" v-b-tooltip.hover :title="meta.MetaKey">{{ meta.MetaKey }}</div>
                                </div>
                                <div style="flex: 1; min-width: 0">
                                    <label class="d-block text-muted small font-weight-bold text-uppercase mb-0" style="font-size: 0.65rem">Değer</label>
                                    <div class="text-truncate text-secondary small" v-b-tooltip.hover :title="meta.MetaValue">{{ meta.MetaValue || "-" }}</div>
                                </div>
                            </div>

                            <div class="d-flex align-items-center justify-content-end mt-1 mt-md-0 action-buttons flex-shrink-0">
                                <b-button
                                    variant="flat-primary"
                                    size="sm"
                                    class="btn-icon rounded-circle p-0 mr-50"
                                    style="width: 24px; height: 24px"
                                    v-b-tooltip.hover
                                    title="Kopyala"
                                    @click="cloneMetaData(meta)"
                                >
                                    <feather-icon icon="CopyIcon" size="14" />
                                </b-button>
                                <b-button
                                    variant="flat-secondary"
                                    size="sm"
                                    class="btn-icon rounded-circle p-0 mr-50"
                                    style="width: 24px; height: 24px"
                                    v-b-tooltip.hover
                                    title="Düzenle"
                                    @click="editMetaData(index)"
                                >
                                    <feather-icon icon="Edit2Icon" size="14" />
                                </b-button>
                                <b-button
                                    variant="flat-danger"
                                    size="sm"
                                    class="btn-icon rounded-circle p-0"
                                    style="width: 24px; height: 24px"
                                    v-b-tooltip.hover
                                    title="Sil"
                                    @click="removeMetaData(index)"
                                >
                                    <feather-icon icon="TrashIcon" size="14" />
                                </b-button>
                            </div>
                        </div>
                    </b-col>
                </b-row>
            </div>
        </b-card>

        <meta-data-modal
            :modal-id="'file-meta-modal'"
            :item="currentMetaItem"
            :is-editing="editingMetaIndex !== -1"
            :languages="languages"
            :group-options="metaGroupOptions"
            @save="handleSaveMetaData"
        />
    </div>
</template>

<script>
import { BRow, BCol, BFormGroup, BFormInput, BFormTextarea, BFormCheckbox, BTabs, BTab, BButton, BCard, BBadge, VBTooltip } from "bootstrap-vue";
import MetaDataModal from "../modals/MetaDataModal.vue";

export default {
    name: "FileEditForm",
    components: {
        BRow,
        BCol,
        BFormGroup,
        BFormInput,
        BFormTextarea,
        BFormCheckbox,
        BTabs,
        BTab,
        BButton,
        BCard,
        BBadge,
        MetaDataModal,
    },
    directives: {
        "b-tooltip": VBTooltip,
    },
    props: {
        value: { type: Object, required: true },
        errors: { type: Object, default: () => ({}) },
        languages: { type: Array, default: () => [] },
    },
    data() {
        return {
            currentMetaItem: {},
            editingMetaIndex: -1,
        };
    },
    computed: {
        localValue: {
            get() {
                return this.value;
            },
            set(val) {
                this.$emit("input", val);
            },
        },
        metaGroupOptions() {
            if (!this.localValue.metaData) return [];
            const groups = [...new Set(this.localValue.metaData.map((m) => m.MetaGroup).filter((g) => g))];
            return groups.map((g) => ({ label: g, value: g }));
        },
    },
    created() {
        this.initializeTranslations();
    },
    watch: {
        languages() {
            this.initializeTranslations();
        },
    },
    methods: {
        initializeTranslations() {
            if (!this.localValue.translations) {
                this.$set(this.localValue, "translations", []);
            }
            if (this.languages && this.languages.length > 0) {
                this.languages.forEach((lang) => {
                    if (!this.localValue.translations.find((t) => t.LanguageCode === lang.LanguageCode)) {
                        this.localValue.translations.push({
                            LanguageCode: lang.LanguageCode,
                            Title: "",
                            Alt: "",
                            Description: "",
                        });
                    }
                });
            }
        },
        getTranslation(languageCode) {
            let translation = this.localValue.translations?.find((t) => t.LanguageCode === languageCode);
            if (!translation) return { LanguageCode: languageCode, Title: "", Alt: "", Description: "" };
            return translation;
        },
        updateTranslation(languageCode, field, value) {
            const index = this.localValue.translations.findIndex((t) => t.LanguageCode === languageCode);
            if (index !== -1) {
                const updatedItem = { ...this.localValue.translations[index] };
                const propName = field.charAt(0).toUpperCase() + field.slice(1);
                updatedItem[propName] = value;
                this.$set(this.localValue.translations, index, updatedItem);
            } else {
                const newT = { LanguageCode: languageCode, Title: "", Alt: "", Description: "" };
                const propName = field.charAt(0).toUpperCase() + field.slice(1);
                newT[propName] = value;
                this.localValue.translations.push(newT);
            }
        },

        openMetaModal(item = null, index = -1) {
            this.editingMetaIndex = index;
            this.currentMetaItem = item
                ? { ...item }
                : {
                      MetaKey: "",
                      MetaValue: "",
                      LanguageCode: null,
                      MetaGroup: "",
                      SortOrder: this.localValue.metaData ? this.localValue.metaData.length : 0,
                  };
            this.$bvModal.show("file-meta-modal");
        },
        addMetadata() {
            this.openMetaModal();
        },
        editMetaData(index) {
            this.openMetaModal(this.localValue.metaData[index], index);
        },
        cloneMetaData(source) {
            const newItem = { ...source, MetaKey: "", MetaValue: "", SortOrder: this.localValue.metaData.length };
            this.openMetaModal(newItem, -1);
        },
        handleSaveMetaData(item) {
            if (!this.localValue.metaData) this.$set(this.localValue, "metaData", []);
            if (this.editingMetaIndex > -1) {
                this.$set(this.localValue.metaData, this.editingMetaIndex, item);
            } else {
                this.localValue.metaData.push(item);
            }
        },
        removeMetaData(index) {
            this.$confirm("Sil", "Bu meta verisini silmek istediğinize emin misiniz?").then((res) => {
                if (res) this.localValue.metaData.splice(index, 1);
            });
        },
    },
};
</script>

<style scoped>
.theme-item {
    background-color: #ffffff;
    transition: all 0.3s ease;
}
.dark-layout .theme-item {
    background-color: #161d31 !important;
    border-color: #3b4253 !important;
}
.dark-layout .theme-item .text-dark {
    color: #d0d2d6 !important;
}
.dark-layout .theme-item .text-muted {
    color: #676d7d !important;
}
.dark-layout .theme-item .text-secondary {
    color: #b4b7bd !important;
}

.hover-card {
    transition: all 0.2s ease;
    border: 1px solid #e2e2e2 !important;
}
.hover-card:hover {
    border-color: #7367f0 !important;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.05) !important;
}
.dark-layout .hover-card:hover {
    background-color: #202943 !important;
    box-shadow: 0 5px 20px rgba(0, 0, 0, 0.6) !important;
    border-color: #7367f0 !important;
}

.dark-layout .hover-bg-light:hover {
    background-color: #3b4253 !important;
    color: #d0d2d6 !important;
}

@media (min-width: 768px) {
    .border-left-md {
        border-left: 1px solid #ebe9f1;
    }
}
.dark-layout .border-left-md {
    border-color: #3b4253;
}
</style>
